import React, { useEffect, useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableExpandedRows,
  DataTableRowClickEvent,
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { SelectButton } from 'primereact/selectbutton';
import { Tag } from 'primereact/tag';
import { JournalEntryStatus } from '../../../../../api/types/accounting/journal-entries/journal-entry-entity';
import { JournalEntryLineEntity } from '../../../../../api/types/accounting/journal-entries/journal-entry-line-entity';
import { JournalEntryReportSummaryEntity } from '../../../../../api/types/accounting/journal-entries/journal-entry-report-details-response';
import {
  JournalEntryLineWithAccountEntity,
  JournalEntryReportDetailsWithLinesEntity,
} from '../../../../../api/types/accounting/journal-entries/journal-entry-report-details-with-lines-entity';
import { PaymentMethodsDb } from '../../../../../api/types/students/payment-services/user-payments/user-payment-service-with-price-list';
import { formatDate } from '../../../../../utils/date-format';
import { downloadJournalEntryReportDatOperation } from '../../operations/download-journal-entry-report-dat-operation';
import { exportJournalEntryReportFlatDataExcelOperation } from '../../operations/export-journal-entry-report-flat-data-excel-operation';
import { exportJournalEntryReportFlatDataPdfOperation } from '../../operations/export-journal-entry-report-flat-data-pdf-operation';
import { getJournalEntryReportDetailsOperation } from '../../operations/get-journal-entry-report-details-operation';

interface JournalEntryReportDetailsDialogProps {
  visible: boolean;
  reportId: number | null;
  onHide: () => void;
}

type ViewMode = 'entries' | 'lines';

const statusLabel = (status: JournalEntryStatus): string => {
  switch (status) {
    case JournalEntryStatus.PENDING:
      return 'ממתין לדיווח';
    case JournalEntryStatus.EXPORTED:
      return 'דווח';
    default:
      return status;
  }
};

const statusSeverity = (
  status: JournalEntryStatus,
): 'success' | 'warning' | 'danger' | 'info' => {
  switch (status) {
    case JournalEntryStatus.PENDING:
      return 'warning';
    case JournalEntryStatus.EXPORTED:
      return 'success';
    default:
      return 'info';
  }
};

const paymentMethodLabel = (paymentMethod: PaymentMethodsDb): string => {
  switch (paymentMethod) {
    case PaymentMethodsDb.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethodsDb.Cash:
      return 'מזומן';
    case PaymentMethodsDb.Discount:
      return 'הנחה';
    case PaymentMethodsDb.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethodsDb.Scholarship:
      return 'מלגה';
    case PaymentMethodsDb.Check:
      return 'המחאה';
    default:
      return paymentMethod;
  }
};

const sourceLabel = (
  entry: JournalEntryReportDetailsWithLinesEntity,
): string => {
  if (entry.userPaymentId !== null) return 'תשלום סטודנט';
  if (entry.userPaymentCheckId !== null) return 'המחאת סטודנט';
  if (entry.crmPaymentId !== null) return 'תשלום CRM';
  if (entry.crmCheckId !== null) return 'המחאה CRM';
  if (entry.depositId !== null) return 'הפקדה';
  if (entry.depositPaymentId !== null) return 'תשלום בהפקדה';
  return '—';
};

const totalDebit = (lines: JournalEntryLineEntity[] | undefined): number =>
  (lines ?? []).reduce(
    (sum, l) => sum + (typeof l.debit === 'number' ? l.debit : 0),
    0,
  );

const formatAccountLabel = (line: JournalEntryLineWithAccountEntity): string =>
  `${line.accountName} (${line.accountCode})`;

const debitAccountNames = (
  lines: JournalEntryLineWithAccountEntity[] | undefined,
): string[] =>
  (lines ?? [])
    .filter((l) => typeof l.debit === 'number' && l.debit !== 0)
    .map(formatAccountLabel);

const creditAccountNames = (
  lines: JournalEntryLineWithAccountEntity[] | undefined,
): string[] =>
  (lines ?? [])
    .filter((l) => typeof l.credit === 'number' && l.credit !== 0)
    .map(formatAccountLabel);

const renderStackedAccountNames = (accountNames: string[]): React.ReactNode => {
  if (accountNames.length === 0) return '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      {accountNames.map((accountName, idx) => (
        <span key={`${accountName}-${idx}`}>{accountName}</span>
      ))}
    </div>
  );
};

const viewOptions = [
  { label: 'פעולות', value: 'entries' as ViewMode },
  { label: 'פקודות יומן', value: 'lines' as ViewMode },
];

export const JournalEntryReportDetailsDialog: React.FC<
  JournalEntryReportDetailsDialogProps
> = ({ visible, reportId, onHide }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [downloadingDat, setDownloadingDat] = useState<boolean>(false);
  const [exportingPdf, setExportingPdf] = useState<boolean>(false);
  const [exportingExcel, setExportingExcel] = useState<boolean>(false);
  const [report, setReport] = useState<JournalEntryReportSummaryEntity | null>(
    null,
  );
  const [entries, setEntries] = useState<
    JournalEntryReportDetailsWithLinesEntity[]
  >([]);
  const [viewMode, setViewMode] = useState<ViewMode>('entries');
  const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows>({});

  useEffect(() => {
    if (!visible || reportId === null) {
      setReport(null);
      setEntries([]);
      setExpandedRows({});
      setViewMode('entries');
      return;
    }
    getJournalEntryReportDetailsOperation(
      reportId,
      setReport,
      setEntries,
      setLoading,
    );
  }, [visible, reportId]);

  // Flattened lines view: every line from every entry, kept in the original
  // order, with `journalEntryId` so the user can trace back which entry the
  // line belongs to.
  const flatLines = useMemo<JournalEntryLineWithAccountEntity[]>(
    () =>
      entries.flatMap((entry) =>
        (entry.lines ?? []).map((line) => ({
          ...line,
          // backend already provides journalEntryId on each line, but fall back
          // to the parent entry id just in case
          journalEntryId: line.journalEntryId ?? entry.id,
        })),
      ),
    [entries],
  );

  const entriesTotalDebit = useMemo(
    () => entries.reduce((sum, entry) => sum + totalDebit(entry.lines), 0),
    [entries],
  );

  const toggleEntryExpansion = (entryId: number) => {
    setExpandedRows((prev) => {
      const next = { ...(prev as DataTableExpandedRows) };
      if (next[entryId] !== undefined) {
        delete next[entryId];
      } else {
        next[entryId] = true;
      }
      return next;
    });
  };

  const onEntryRowClick = (event: DataTableRowClickEvent) => {
    const row = event.data as JournalEntryReportDetailsWithLinesEntity;
    if (!row || typeof row.id !== 'number') return;
    toggleEntryExpansion(row.id);
  };

  const entryExpanderTemplate = (
    row: JournalEntryReportDetailsWithLinesEntity,
  ) => {
    const isExpanded =
      (expandedRows as DataTableExpandedRows)?.[row.id] !== undefined;
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggleEntryExpansion(row.id);
        }}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: '14px',
          padding: '4px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-label={isExpanded ? 'כווץ' : 'הרחב'}
      >
        <i
          className={`pi ${isExpanded ? 'pi-chevron-down' : 'pi-chevron-left'}`}
        />
      </button>
    );
  };

  const lineExpansionTemplate = (
    row: JournalEntryReportDetailsWithLinesEntity,
  ) => (
    <div style={{ padding: '8px 12px', background: '#f8fafc' }}>
      <DataTable
        value={row.lines ?? []}
        rowHover
        emptyMessage={
          <div style={{ textAlign: 'right' }}>אין פקודות יומן בפעולה זו</div>
        }
        dataKey="id"
        tableStyle={{ direction: 'rtl' }}
      >
        <Column
          field="accountName"
          header="חשבון"
          style={{ textAlign: 'right', width: '110px' }}
          headerStyle={{ textAlign: 'right' }}
          body={(line: JournalEntryLineWithAccountEntity) =>
            formatAccountLabel(line)
          }
        />
        <Column
          field="debit"
          header="חובה"
          style={{ textAlign: 'right', width: '120px' }}
          headerStyle={{ textAlign: 'right' }}
          body={(line: JournalEntryLineEntity) =>
            typeof line.debit === 'number' ? line.debit.toFixed(2) : '0.00'
          }
        />
        <Column
          field="credit"
          header="זכות"
          style={{ textAlign: 'right', width: '120px' }}
          headerStyle={{ textAlign: 'right' }}
          body={(line: JournalEntryLineEntity) =>
            typeof line.credit === 'number' ? line.credit.toFixed(2) : '0.00'
          }
        />
        <Column
          field="description"
          header="תיאור"
          style={{ textAlign: 'right' }}
          headerStyle={{ textAlign: 'right' }}
          body={(line: JournalEntryLineEntity) =>
            line.description?.trim() ? line.description : '—'
          }
        />
      </DataTable>
    </div>
  );

  const handleDownloadDat = () => {
    if (reportId === null) return;
    downloadJournalEntryReportDatOperation(reportId, setDownloadingDat);
  };

  const handleExportPdf = () => {
    if (reportId === null) return;
    exportJournalEntryReportFlatDataPdfOperation(reportId, setExportingPdf);
  };

  const handleExportExcel = () => {
    if (reportId === null) return;
    exportJournalEntryReportFlatDataExcelOperation(reportId, setExportingExcel);
  };

  const anyExportInFlight = exportingPdf || exportingExcel || downloadingDat;

  const dialogFooter = (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
      <Button
        icon="pi pi-file-pdf"
        tooltip="ייצוא לPDF"
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
        onClick={handleExportPdf}
        loading={exportingPdf}
        disabled={reportId === null || anyExportInFlight || loading}
        style={{
          backgroundColor: '#dc2626',
          borderColor: '#dc2626',
          height: '32px',
          width: '48px',
        }}
      />
      <Button
        icon="pi pi-file-excel"
        tooltip="ייצוא לאקסל"
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
        onClick={handleExportExcel}
        loading={exportingExcel}
        disabled={reportId === null || anyExportInFlight || loading}
        style={{
          backgroundColor: '#10b981',
          borderColor: '#10b981',
          height: '32px',
          width: '48px',
        }}
      />
      <Button
        icon="pi pi-download"
        tooltip="הורדת קובץ לחשבשבת"
        tooltipOptions={{ position: 'top', showOnDisabled: true }}
        onClick={handleDownloadDat}
        loading={downloadingDat}
        disabled={reportId === null || anyExportInFlight || loading}
        style={{
          backgroundColor: '#2563eb',
          borderColor: '#2563eb',
          height: '32px',
          width: '48px',
        }}
      />
      <Button
        label="סגור"
        icon="pi pi-times"
        style={{ height: '32px' }}
        className="p-button-outlined p-button-secondary"
        onClick={onHide}
      />
    </div>
  );

  return (
    <Dialog
      header={reportId !== null ? `פרטי דיווח #${reportId}` : 'פרטי דיווח'}
      visible={visible}
      onHide={onHide}
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      style={{ width: '900px', maxWidth: '95vw' }}
      footer={dialogFooter}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          direction: 'rtl',
        }}
      >
        {loading && entries.length === 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '24px',
            }}
          >
            <ProgressSpinner style={{ width: '50px', height: '50px' }} />
          </div>
        ) : (
          <>
            {report && (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                  gap: '12px',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    מזהה דיווח
                  </span>
                  <span
                    style={{
                      fontSize: '0.95rem',
                      color: '#0f172a',
                      fontWeight: 600,
                    }}
                  >
                    #{report.id}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    תאריך יצירה
                  </span>
                  <span
                    style={{
                      fontSize: '0.95rem',
                      color: '#0f172a',
                      fontWeight: 600,
                    }}
                  >
                    {report.createdAt
                      ? formatDate(report.createdAt as Date)
                      : 'חסר'}
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px',
                  }}
                >
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    נוצר על ידי
                  </span>
                  <span
                    style={{
                      fontSize: '0.95rem',
                      color: '#0f172a',
                      fontWeight: 600,
                    }}
                  >
                    {report.createdByName?.trim()
                      ? report.createdByName
                      : 'חסר'}
                  </span>
                </div>
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  flexWrap: 'wrap',
                  color: '#475569',
                  fontSize: '0.9rem',
                }}
              >
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '999px',
                    background: '#eef2ff',
                    border: '1px solid #c7d2fe',
                    color: '#3730a3',
                    fontWeight: 600,
                  }}
                >
                  פעולות: {entries.length.toLocaleString()}
                </span>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '999px',
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    color: '#065f46',
                    fontWeight: 600,
                  }}
                >
                  סך פקודות יומן: {flatLines.length.toLocaleString()}
                </span>
                <span
                  style={{
                    padding: '4px 10px',
                    borderRadius: '999px',
                    background: '#fffbeb',
                    border: '1px solid #fde68a',
                    color: '#92400e',
                    fontWeight: 600,
                  }}
                >
                  סכום כולל: {entriesTotalDebit.toFixed(2)}
                </span>
              </div>

              <SelectButton
                value={viewMode}
                options={viewOptions}
                onChange={(e) => {
                  if (e.value) setViewMode(e.value as ViewMode);
                }}
                dir="ltr"
              />
            </div>

            {viewMode === 'entries' ? (
              <DataTable
                value={entries}
                loading={loading}
                rowHover
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                emptyMessage={
                  <div style={{ textAlign: 'right' }}>
                    לא נמצאו פעולות בדיווח זה
                  </div>
                }
                dataKey="id"
                tableStyle={{ direction: 'rtl' }}
                expandedRows={expandedRows}
                onRowToggle={(e) =>
                  setExpandedRows(e.data as DataTableExpandedRows)
                }
                rowExpansionTemplate={lineExpansionTemplate}
                onRowClick={onEntryRowClick}
                rowClassName={() => 'cursor-pointer'}
              >
                <Column
                  expander={false}
                  body={entryExpanderTemplate}
                  style={{ width: '3rem', textAlign: 'center' }}
                  headerStyle={{ width: '3rem' }}
                />
                {/*                <Column
                  field="entryDate"
                  header="תאריך פקודה"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    row.entryDate ? formatDate(row.entryDate as Date) : 'חסר'
                  }
                />*/}
                <Column
                  field="paymentMethodType"
                  header="אמצעי תשלום"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    paymentMethodLabel(row.paymentMethodType)
                  }
                />
                <Column
                  field="status"
                  header="סטטוס"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) => (
                    <Tag
                      value={statusLabel(row.status)}
                      severity={statusSeverity(row.status)}
                    />
                  )}
                />
                <Column
                  header="חשבון חובה"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    renderStackedAccountNames(debitAccountNames(row.lines))
                  }
                />
                <Column
                  header="חשבון זכות"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    renderStackedAccountNames(creditAccountNames(row.lines))
                  }
                />
                <Column
                  header="סכום"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    totalDebit(row.lines).toFixed(2)
                  }
                />
                <Column
                  field="valueDate"
                  header="ת.ערך"
                  style={{ textAlign: 'right', width: '120px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    row.valueDate ? formatDate(row.valueDate as Date) : 'חסר'
                  }
                />
                <Column
                  field="documentNumber"
                  header="מספר מסמך"
                  style={{ textAlign: 'right', width: '120px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    row.documentNumber != null ? row.documentNumber : 'חסר'
                  }
                />
                <Column
                  field="paymentReference"
                  header="מזהה תשלום"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    row.paymentReference?.trim() ? row.paymentReference : 'חסר'
                  }
                />
                <Column
                  header="מקור"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    sourceLabel(row)
                  }
                />
                <Column
                  field="createdAt"
                  header="תאריך יצירה"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(row: JournalEntryReportDetailsWithLinesEntity) =>
                    row.createdAt ? formatDate(row.createdAt as Date) : 'חסר'
                  }
                />
              </DataTable>
            ) : (
              <DataTable
                value={flatLines}
                loading={loading}
                rowHover
                paginator
                rows={20}
                rowsPerPageOptions={[10, 20, 50, 100]}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                emptyMessage={
                  <div style={{ textAlign: 'right' }}>
                    לא נמצאו פקודות יומן בדיווח זה
                  </div>
                }
                dataKey="id"
                tableStyle={{ direction: 'rtl' }}
              >
                <Column
                  field="accountName"
                  header="חשבון"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(line: JournalEntryLineWithAccountEntity) =>
                    formatAccountLabel(line)
                  }
                />
                <Column
                  field="debit"
                  header="חובה"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(line: JournalEntryLineEntity) =>
                    typeof line.debit === 'number'
                      ? line.debit.toFixed(2)
                      : '0.00'
                  }
                />
                <Column
                  field="credit"
                  header="זכות"
                  style={{ textAlign: 'right', width: '110px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(line: JournalEntryLineEntity) =>
                    typeof line.credit === 'number'
                      ? line.credit.toFixed(2)
                      : '0.00'
                  }
                />
                <Column
                  field="description"
                  header="תיאור"
                  style={{ textAlign: 'right' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(line: JournalEntryLineEntity) =>
                    line.description?.trim() ? line.description : '—'
                  }
                />
                <Column
                  field="createdAt"
                  header="תאריך יצירה"
                  style={{ textAlign: 'right', width: '130px' }}
                  headerStyle={{ textAlign: 'right' }}
                  body={(line: JournalEntryLineEntity) =>
                    line.createdAt ? formatDate(line.createdAt as Date) : 'חסר'
                  }
                />
              </DataTable>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
};
